import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import LocationsList from './pages/LocationsList';
import LocationDetail from './pages/LocationDetail';
import ItemsList from './pages/ItemsList';
import ItemDetail from './pages/ItemDetail';
import LocationForm from './pages/LocationForm';
import ItemForm from './pages/ItemForm';
import DatabaseManager from './pages/DatabaseManager';
import QRScanner from './components/QRScanner';
import Guide from './pages/Guide';
import QuickAdd from './pages/QuickAdd';
import SearchResults from './pages/SearchResults';
import ShareManage from './pages/ShareManage';
import GuestShare from './pages/GuestShare';

// The guest pages (/share/...) are public and read-only: no app navigation bar
function AppNavbar(props) {
  const { pathname } = useLocation();
  return pathname.startsWith('/share/') ? null : <Navbar {...props} />;
}

function App() {
  const [currentDatabase, setCurrentDatabase] = useState('default.db');
  const [databases, setDatabases] = useState([]);
  const [multiDatabase, setMultiDatabase] = useState(true);

  useEffect(() => {
    // guests only see published shares, so they must not call the private API
    if (window.location.pathname.startsWith('/share/')) return;
    loadConfig();
    loadDatabases();
    loadCurrentDatabase();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/config');
      if (!response.ok) return;
      const data = await response.json();
      setMultiDatabase(data.multiDatabase !== false);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadDatabases = async () => {
    try {
      const response = await fetch('/api/databases');
      const data = await response.json();
      const list = Array.isArray(data) ? data : data?.databases ?? [];
      if (!response.ok) {
        console.error('Error loading databases:', data.error || data);
      }
      setDatabases(list);
    } catch (error) {
      console.error('Error loading databases:', error);
      setDatabases([]);
    }
  };

  const loadCurrentDatabase = async () => {
    try {
      const response = await fetch('/api/databases/current');
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (data?.name) {
        setCurrentDatabase(data.name);
      }
    } catch (error) {
      console.error('Error loading current database:', error);
    }
  };

  const switchDatabase = async (dbName) => {
    try {
      const response = await fetch('/api/databases/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: dbName })
      });
      if (response.ok) {
        setCurrentDatabase(dbName);
        await loadDatabases();
        window.location.reload();
      } else {
        const errorData = await response.json();
        console.error('Error switching database:', errorData.error || errorData);
      }
    } catch (error) {
      console.error('Error switching database:', error);
    }
  };

  return (
    <Router>
      <div className="App">
        <AppNavbar 
          currentDatabase={currentDatabase} 
          databases={databases}
          onDatabaseSwitch={switchDatabase}
          multiDatabase={multiDatabase}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home multiDatabase={multiDatabase} />} />
            <Route path="/locations" element={<LocationsList />} />
            <Route path="/locations/new" element={<LocationForm />} />
            <Route path="/locations/:id/edit" element={<LocationForm />} />
            <Route path="/locations/:id/quick-add" element={<QuickAdd />} />
            <Route path="/locations/:id" element={<LocationDetail />} />
            <Route path="/items" element={<ItemsList />} />
            <Route path="/items/new" element={<ItemForm />} />
            <Route path="/items/:id/edit" element={<ItemForm />} />
            <Route path="/items/:id" element={<ItemDetail />} />
            {multiDatabase && <Route path="/databases" element={<DatabaseManager databases={databases} onRefresh={loadDatabases} />} />}
            <Route path="/scan" element={<QRScanner />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/locations/:id/share" element={<ShareManage />} />
            <Route path="/share/:token" element={<GuestShare />} />
            <Route path="/guide/:kind/:id" element={<Guide />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
